import { gql } from '../../../core/graphql-client.js';
import { router } from '../../../core/router.js';
import { toast } from '../../../core/event-bus.js';
import type { Quarter, Saturday } from '../../../types.js';

export class QuarterEditPage extends HTMLElement {
  private quarter: Quarter | null = null;

  get year(): number { return Number(this.getAttribute('year')); }
  get num(): number { return Number(this.getAttribute('num')); }

  connectedCallback(): void {
    this.innerHTML = '<loading-spinner text="Ładowanie kwartału..."></loading-spinner>';
    this.loadData();
  }

  private async loadData(): Promise<void> {
    try {
      const data = await gql.query<{ quarter: Quarter | null }>(`
        query($year: Int!, $qn: Int!) {
          quarter(year: $year, quarterNum: $qn) {
            year quarterNum label isActive
            saturdays { date eventNote isSkipped sortOrder }
          }
        }
      `, { year: this.year, qn: this.num });

      this.quarter = data.quarter;
      if (!this.quarter) {
        this.innerHTML = '<div class="page"><p class="error-text">Kwartał nie znaleziony.</p></div>';
        return;
      }
      this.render();
    } catch (e) {
      this.innerHTML = `<div class="page"><p class="error-text">Błąd: ${e instanceof Error ? e.message : e}</p></div>`;
    }
  }

  private render(): void {
    const q = this.quarter!;
    const saturdays = q.saturdays ?? [];

    const formatDate = (iso: string) => {
      const d = new Date(iso);
      return d.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' });
    };

    this.innerHTML = `
      <div class="page">
        <div class="page-header">
          <h1>${q.label}</h1>
          <button class="btn btn-secondary" id="back-btn">← Powrót</button>
        </div>
        <p class="quarter-info">
          ${q.isActive ? '<span class="chip chip-success">Aktywny</span>' : '<span class="chip">Nieaktywny</span>'}
          <span class="saturday-count">${saturdays.length} sobót</span>
        </p>
        <div class="saturday-list">
          ${saturdays.map(s => `
            <div class="card saturday-card ${s.isSkipped ? 'saturday-skipped' : ''}" data-date="${s.date}">
              <div class="saturday-header">
                <span class="saturday-date">${formatDate(s.date)}</span>
                <label class="switch">
                  <input type="checkbox" class="skip-toggle" data-date="${s.date}" ${s.isSkipped ? 'checked' : ''}>
                  <span>Pomiń</span>
                </label>
              </div>
              <div class="saturday-note-row">
                <input type="text" class="note-input" data-date="${s.date}" placeholder="Notatka (np. komunia, święto...)" value="${s.eventNote ?? ''}">
                <button class="btn btn-secondary btn-sm save-note-btn" data-date="${s.date}">Zapisz</button>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
      <style>
        .quarter-info { display: flex; align-items: center; gap: var(--space-sm); margin-bottom: var(--space-md); }
        .saturday-count { font-size: .875rem; color: var(--color-on-surface-muted); }
        .saturday-list { display: flex; flex-direction: column; gap: var(--space-sm); }
        .saturday-card { transition: opacity .2s; }
        .saturday-skipped { opacity: .5; }
        .saturday-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: var(--space-sm); }
        .saturday-date { font-weight: 500; }
        .saturday-note-row { display: flex; gap: var(--space-sm); align-items: center; }
        .note-input { flex: 1; }
        .chip-success { background: var(--color-success-bg); color: var(--color-success); border-color: var(--color-success); }
        .btn-sm { padding: var(--space-xs) var(--space-sm); font-size: .8rem; min-height: 32px; }
        .error-text { color: var(--color-error); }
      </style>
    `;

    // Back button
    this.querySelector('#back-btn')!.addEventListener('click', () => {
      router.navigate('/admin/quarters');
    });

    // Skip toggles
    this.querySelectorAll('.skip-toggle').forEach(toggle => {
      toggle.addEventListener('change', async () => {
        const el = toggle as HTMLInputElement;
        const date = el.dataset.date!;
        const isSkipped = el.checked;

        try {
          await gql.query(`
            mutation($date: String!, $input: UpdateSaturdayInput!) {
              updateSaturday(date: $date, input: $input) { date isSkipped }
            }
          `, { date, input: { isSkipped } });

          const card = this.querySelector(`.saturday-card[data-date="${date}"]`)!;
          card.classList.toggle('saturday-skipped', isSkipped);
          toast(isSkipped ? 'Sobota pominięta' : 'Sobota przywrócona', 'success');
        } catch (err) {
          el.checked = !isSkipped; // revert
          toast(`Błąd: ${err instanceof Error ? err.message : err}`, 'error');
        }
      });
    });

    // Save note buttons
    this.querySelectorAll('.save-note-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const el = btn as HTMLElement;
        const date = el.dataset.date!;
        const input = this.querySelector(`.note-input[data-date="${date}"]`) as HTMLInputElement;
        const eventNote = input.value.trim() || null;

        try {
          await gql.query(`
            mutation($date: String!, $input: UpdateSaturdayInput!) {
              updateSaturday(date: $date, input: $input) { date eventNote }
            }
          `, { date, input: { eventNote } });
          toast('Notatka zapisana', 'success');
        } catch (err) {
          toast(`Błąd: ${err instanceof Error ? err.message : err}`, 'error');
        }
      });
    });
  }
}

customElements.define('quarter-edit-page', QuarterEditPage);
